import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Mail, Loader2, ExternalLink } from 'lucide-react';
import { authedFetch } from '../lib/authedFetch';
import TrackingNumber from './TrackingNumber';

/**
 * "Where did my mail get to" — the Deal Tracker's view of your own
 * requests.
 *
 * Asking for an offer to be mailed used to be the end of what you could
 * see. The piece went into the queue, somebody printed it, and the only
 * way to find out whether that had happened was to ask them. The
 * mailroom screen shows the whole queue, which is right for the person
 * working it and wrong for the person who asked: they want their three
 * pieces, not everybody's forty.
 *
 * So it asks the server for its own, rather than pulling the queue down
 * and filtering here. Somebody checking on their own offer has no
 * business fetching every seller's home address to find it.
 *
 * Portalled, for the reason every other menu on this page is: the kanban
 * columns paint over anything the header stacks inside itself, whatever
 * its z-index, because they are in a different stacking context.
 */

const MAIL_TYPE_LABEL = {
  certified_pdf: 'Offer',
  closing_packet: 'Closing documents',
  deed_w9_return: 'Mineral deed + W-9',
};

export default function MyMailMenu({ width = 340, margin = 12, zIndex = 2147483000, compact = false }) {
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [position, setPosition] = useState({ left: margin, top: 96 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authedFetch('/api/mailroom/queue?mine=1');
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.error || `Could not load your mail (${response.status})`);
      setJobs(Array.isArray(json.jobs) ? json.jobs : []);
      setError('');
    } catch (err) {
      setError(err?.message || 'Could not load your mail.');
    } finally {
      setLoading(false);
    }
  }, []);

  // The count is what makes the button worth having, so it is fetched
  // whether or not anybody opens the menu.
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    load();
    const onPointerDown = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => { if (event.key === 'Escape') setOpen(false); };
    const place = () => {
      const button = triggerRef.current;
      if (!button || typeof window === 'undefined') return;
      const rect = button.getBoundingClientRect();
      const panelWidth = Math.min(width, window.innerWidth - (margin * 2));
      setPosition({
        left: Math.max(margin, Math.min(rect.right - panelWidth, window.innerWidth - panelWidth - margin)),
        top: Math.max(margin, rect.bottom + 4),
      });
    };
    place();
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, width, margin, load]);

  // What is still in somebody else's hands. Delivered pieces are in the
  // list but not in the count — the point of the number is "there is
  // something of mine nobody has sent yet".
  const waiting = jobs.filter((job) => job.status === 'queued' || job.status === 'submitted');

  // Nothing ever requested, nothing to say. The button appears the first
  // time somebody asks for something to be mailed.
  if (!jobs.length && !loading && !error) return null;

  return (
    <div ref={triggerRef} style={{ position: 'relative', display: 'flex', flexShrink: 0 }}>
      <button
        type="button"
        data-hover-tick
        data-my-mail-open
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        title="Mail you asked to go out, and whether it has"
        style={{
          // Sized to its neighbours in the app header — Improve and Title
          // are 28 high and drop their label on a phone, and a row of
          // chrome buttons that do not line up reads as three accidents
          // rather than one bar.
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          height: 28, padding: compact ? '0 7px' : '0 10px',
          background: waiting.length ? 'rgba(245,158,11,0.18)' : 'rgba(255,255,255,0.06)',
          border: `1px solid ${waiting.length ? 'rgba(245,158,11,0.55)' : 'rgba(91,191,239,0.35)'}`,
          borderRadius: 0, color: '#f1f5f9', fontSize: '0.74rem', fontWeight: 500,
          cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, lineHeight: 1,
        }}
      >
        <Mail size={15} />
        {compact ? null : 'My mail'}
        {/* The count survives compact: it is the reason the button is
            worth looking at, and on a phone it is the only thing on it. */}
        {waiting.length > 0 && (
          <span style={{
            background: '#f59e0b', color: '#1f2937', borderRadius: 999,
            padding: '0 6px', fontSize: '0.68rem', fontWeight: 800,
          }}>
            {waiting.length}
          </span>
        )}
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={panelRef}
          data-my-mail-panel
          style={{
            position: 'fixed', left: position.left, top: position.top, zIndex,
            width, maxWidth: `calc(100vw - ${margin * 2}px)`,
            maxHeight: `calc(100vh - ${margin * 2}px)`, overflowY: 'auto',
            background: '#0f1d33', border: '1px solid rgba(91,191,239,0.35)',
            boxShadow: '0 18px 40px rgba(0,0,0,0.45)',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '0.5rem 0.75rem',
            fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.6px',
            textTransform: 'uppercase', color: '#7b9cc0',
            borderBottom: '1px solid rgba(91,191,239,0.18)',
          }}>
            Mail you asked for
            {loading && <Loader2 size={12} className="spin" />}
          </div>

          {error && (
            <div style={{ padding: '0.6rem 0.75rem', color: '#fca5a5', fontSize: '0.76rem' }}>{error}</div>
          )}

          {!error && jobs.length === 0 && !loading && (
            <div style={{ padding: '0.7rem 0.75rem', color: '#7b9cc0', fontSize: '0.78rem' }}>
              You have not asked for anything to be mailed.
            </div>
          )}

          {jobs.map((job) => (
            <div key={job.id} style={{ padding: '0.55rem 0.75rem', borderBottom: '1px solid rgba(91,191,239,0.12)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ flex: 1, minWidth: 0, color: '#f1f5f9', fontSize: '0.84rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {job.recipient?.name || 'Unnamed recipient'}
                </span>
                <StageChip job={job} />
              </div>
              <div style={{ color: '#7b9cc0', fontSize: '0.72rem', marginTop: 2 }}>
                {MAIL_TYPE_LABEL[job.mailType] || job.mailType}
                {job.trackingNumber ? (
                  <>
                    {' \u00b7 '}
                    <TrackingNumber job={job} dark />
                  </>
                ) : ''}
              </div>
            </div>
          ))}

          <a
            href="/mailroom"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '0.55rem 0.75rem', color: '#93c5fd',
              fontSize: '0.76rem', fontWeight: 600, textDecoration: 'none',
            }}
          >
            <ExternalLink size={13} /> Open the mailroom
          </a>
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * Where the piece has got to, in the words somebody who asked for it
 * would use. "Queued" is a database word; "waiting to print" is what is
 * actually true of it.
 */
function StageChip({ job }) {
  const [label, tone] = stageOf(job);
  return (
    <span style={{
      flexShrink: 0, fontSize: '0.66rem', fontWeight: 700,
      letterSpacing: '0.3px', textTransform: 'uppercase',
      color: tone, border: `1px solid ${tone}55`, padding: '0.08rem 0.35rem',
    }}>
      {label}
    </span>
  );
}

function stageOf(job) {
  if (job.status === 'delivered') return ['Delivered', '#10b981'];
  if (job.status === 'in_transit') return ['In the mail', '#5bbfef'];
  if (job.status === 'failed') return ['Failed', '#f87171'];
  if (job.printedAt) return ['Printed', '#a5b4fc'];
  return ['Waiting to print', '#f59e0b'];
}
